import { Button } from 'antd';
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CheckCircleFilled } from '@ant-design/icons';
import {
  showScanningPopup,
  stopBulkScreeningLocal,
  proceedBulkScreening,
  setUploadedFile
} from '../../../../redux/actions/ns/onDemandScreening';
import { BULK_SCANNING_FLOW } from '../../../constants/nsConstants';
import img_ns_scanning from '../../../../assets/img_ns_scanning.png';
import img_ns_scan_stop from '../../../../assets/img_ns_scan_stop.png';
import img_ns_scan_completed from '../../../../assets/img_ns_scan_completed.png';
import img_ns_scan_error from '../../../../assets/img_ns_scan_error.png';
import {
  ModalContentContainer,
  ModalDesc,
  ModalHeader,
  StyledImg,
  StyledModal,
  StyledProgress,
  ErrorHeader,
  ErrorDesc,
  ModalWrapper,
  ErrorDetailsWrapper,
  TableContainer,
  StyledTable,
  StyledCSVLink
} from './styled';
import COLUMNS from './columns';
import { errorDetailsHeaders } from './constants';

const OK_BUTTON_LABELS = {
  [BULK_SCANNING_FLOW.SCAN]: 'Stop Scanning',
  [BULK_SCANNING_FLOW.SCAN_SUCCESS]: 'Proceed'
};

const ScanModal = ({
  dispatch,
  isShowScanModal,
  currentScreeningState,
  screeningPercent,
  errorMessage,
  jobId,
  totalCount,
  invalidData
}) => {
  const [isStopState, setIsStopState] = useState(false);
  const [isCancelState, setIsCancelState] = useState(false);

  const secondaryButtonClick = () => {
    if (isStopState) {
      setIsStopState(false);
      if (isCancelState) {
        setIsCancelState(false);
      }
    } else if (currentScreeningState === BULK_SCANNING_FLOW.SCAN_SUCCESS) {
      if (totalCount !== invalidData?.length) {
        setIsStopState(true);
        setIsCancelState(true);
      } else {
        dispatch(stopBulkScreeningLocal());
      }
    } else if (currentScreeningState === BULK_SCANNING_FLOW.SCAN_FAILED) {
      dispatch(stopBulkScreeningLocal());
    } else {
      dispatch(showScanningPopup(false));
    }
  };

  const primaryButtonClick = () => {
    if (isStopState) {
      dispatch(setUploadedFile(null));
      dispatch(stopBulkScreeningLocal());
    } else if (currentScreeningState === BULK_SCANNING_FLOW.SCAN) {
      setIsStopState(true);
    } else if (currentScreeningState === BULK_SCANNING_FLOW.SCAN_SUCCESS) {
      dispatch(proceedBulkScreening({ jobId }));
    } else {
      dispatch(showScanningPopup(false));
    }
  };

  const cancelButtonLabel = useMemo(() => {
    if (isStopState) {
      return isCancelState ? 'No' : 'Keep Scanning';
    } else if (
      currentScreeningState === BULK_SCANNING_FLOW.SCAN_SUCCESS ||
      currentScreeningState === BULK_SCANNING_FLOW.SCAN_FAILED
    ) {
      return 'Cancel';
    } else {
      return 'Close';
    }
  }, [isStopState, currentScreeningState]);

  const footer = [
    <Button key="back" onClick={secondaryButtonClick}>
      {cancelButtonLabel}
    </Button>,
    ...(isStopState ||
    (currentScreeningState !== BULK_SCANNING_FLOW.SCAN_FAILED &&
      currentScreeningState === BULK_SCANNING_FLOW.SCAN_SUCCESS &&
      totalCount !== invalidData?.length)
      ? [
          <Button key="stop" type="primary" onClick={primaryButtonClick}>
            {isStopState
              ? `Yes ${!isCancelState ? 'Stop' : ''}`
              : OK_BUTTON_LABELS[currentScreeningState]}
          </Button>
        ]
      : [])
  ];

  const renderScanModal = useMemo(
    () => (
      <ModalContentContainer>
        <StyledImg src={img_ns_scanning} />
        <ModalHeader>Scanning your upload</ModalHeader>
        <StyledProgress percent={screeningPercent} />
        <ModalDesc>
          Feel free to close this screen and come back later.
          <br />
          Your scan will still be running in the background.
        </ModalDesc>
      </ModalContentContainer>
    ),
    [screeningPercent]
  );

  const renderStopModal = useMemo(
    () => (
      <ModalContentContainer>
        <StyledImg src={img_ns_scan_stop} />
        <ModalHeader>
          {isCancelState
            ? "Are you sure you don't want to proceed the screening?"
            : 'Are you sure you want to stop the scanning?'}
          <br />
          All current progress will be lost.
        </ModalHeader>
        <StyledProgress percent={screeningPercent} />
      </ModalContentContainer>
    ),
    [screeningPercent, isCancelState]
  );

  const errorData = useMemo(() => {
    return invalidData?.map((data, index) => ({ ...data, key: index }));
  }, [invalidData]);

  const renderSuccessModal = useMemo(
    () =>
      errorData?.length === 0 ? (
        <ModalContentContainer>
          <StyledImg src={img_ns_scan_completed} />
          <ModalHeader>Scan Completed</ModalHeader>
          <ModalDesc>
            All {totalCount} records have been successfully scanned. No errors
            found
          </ModalDesc>
          <StyledProgress percent={screeningPercent} />
        </ModalContentContainer>
      ) : (
        <>
          <ModalContentContainer>
            <StyledImg src={img_ns_scan_error} />
            <ModalHeader>Scan Completed. Errors Found</ModalHeader>
            <ErrorDetailsWrapper>
              <CheckCircleFilled />
              <div>
                <b>
                  {totalCount - errorData?.length} out of {totalCount} records
                </b>
                <br />
                have been successfully validated
              </div>
            </ErrorDetailsWrapper>
            <ModalDesc>
              The remaining {errorData?.length} records have some errors,
              detailed below
            </ModalDesc>
            <StyledProgress percent={screeningPercent} />
          </ModalContentContainer>
          <TableContainer>
            <StyledCSVLink
              float="right"
              filename="Bulk Upload Error Details.csv"
              headers={errorDetailsHeaders}
              data={errorData}>
              download full details
            </StyledCSVLink>
            <StyledTable
              columns={COLUMNS}
              dataSource={errorData}
              pagination={false}
              scroll={{
                y: 160
              }}
            />
          </TableContainer>
        </>
      ),
    [screeningPercent, errorData, totalCount]
  );

  const renderErrorModal = useMemo(
    () => (
      <ModalContentContainer>
        <StyledImg src={img_ns_scan_error} />
        <ErrorHeader>Scan Error</ErrorHeader>
        <ErrorDesc>{errorMessage}</ErrorDesc>
        <StyledProgress percent={screeningPercent} status="exception" />
      </ModalContentContainer>
    ),
    [screeningPercent, errorMessage]
  );

  const MODAL_RENDER = useMemo(
    () => ({
      [BULK_SCANNING_FLOW.SCAN]: renderScanModal,
      [BULK_SCANNING_FLOW.SCAN_SUCCESS]: renderSuccessModal,
      [BULK_SCANNING_FLOW.SCAN_FAILED]: renderErrorModal
    }),
    [renderErrorModal, renderScanModal, renderSuccessModal]
  );

  const onCancelModal = () => {
    if (currentScreeningState === BULK_SCANNING_FLOW.SCAN_FAILED) {
      dispatch(stopBulkScreeningLocal());
    } else {
      dispatch(showScanningPopup(false));
    }
  };

  return (
    <StyledModal
      className="on-demand-screening-button-css"
      visible={isShowScanModal}
      title="Upload Validation Status"
      destroyOnClose={true}
      footer={footer}
      maskClosable={false}
      width={
        currentScreeningState === BULK_SCANNING_FLOW.SCAN_SUCCESS &&
        invalidData?.length > 0 &&
        !isStopState
          ? '600px'
          : '456px'
      }
      onCancel={onCancelModal}
      onOk={primaryButtonClick}>
      <ModalWrapper>
        {isStopState ? renderStopModal : MODAL_RENDER[currentScreeningState]}
      </ModalWrapper>
    </StyledModal>
  );
};

ScanModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  isShowScanModal: PropTypes.bool,
  currentScreeningState: PropTypes.string,
  screeningPercent: PropTypes.number,
  errorMessage: PropTypes.string,
  jobId: PropTypes.number,
  totalCount: PropTypes.number,
  invalidData: PropTypes.array
};

const mapStateToProps = (state) => {
  const {
    bulkScreening: {
      isShowScanModal,
      currentScreeningState,
      screeningPercent,
      errorMessage,
      jobId,
      totalCount,
      invalidData
    }
  } = state.nsOnDemandScreening;
  return {
    isShowScanModal,
    currentScreeningState,
    screeningPercent,
    errorMessage,
    jobId,
    totalCount,
    invalidData
  };
};

export default connect(mapStateToProps)(ScanModal);
